import { useCallback, useEffect, useState } from 'react';

import { useIsLoggedIn } from '@order/AuthMachine';
import type { Customer } from '@order/graphql';

import { useCurrentCustomerQuery } from '../GraphQL/CurrentCustomer.query.generated';

export const useCustomerData = () => {
  const [customer, setCustomer] = useState(emptyCustomer);
  const isCustomerLoggedIn = useIsLoggedIn();

  const [customerResponse, getCustomerData] = useCurrentCustomerQuery({
    pause: true,
    requestPolicy: 'cache-and-network',
  });

  const customerResponseData = customerResponse.data?.currentCustomer;

  const isDataResolved = typeof customerResponse.data !== 'undefined';
  const isFetchingCustomer = customerResponse.fetching;
  const isStaleCustomer = customerResponse.stale;
  const isCustomerLoaded =
    isDataResolved &&
    !customerResponse.fetching &&
    Boolean(customerResponse.data?.currentCustomer?.id);

  // ────────────────────────────────────────────────────────────────────

  const setCustomerData = useCallback(
    (customerData: Partial<Customer> | null | undefined) => {
      if (!customerData) return;
      setCustomer(customerData);
    },
    [],
  );

  const resetCustomerData = useCallback(() => {
    setCustomer(emptyCustomer);
  }, []);

  // ────────────────────────────────────────────────────────────────────

  // fetches the customer data if they are logged in
  // the useEffect being used to prevent duplicated network requests
  useEffect(() => {
    if (!isCustomerLoggedIn) return;
    getCustomerData();
  }, [getCustomerData, isCustomerLoggedIn]);

  useEffect(() => {
    if (isDataResolved) setCustomerData(customerResponseData);
  }, [customerResponseData, isDataResolved, setCustomerData]);

  // ────────────────────────────────────────────────────────────────────

  return {
    customerResponseData,
    customer,
    isFetchingCustomer,
    isStaleCustomer,
    isCustomerLoaded,
    setCustomerData,
    resetCustomerData,
    getCustomerData,
  };
};

// ────────────────────────────────────────────────────────────────────────────────

export const emptyCustomer: Partial<Customer> = {};
